import { useState, useEffect } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { Badge } from './ui/badge';
import { getTimeUntilExpiration } from '../types/job';

interface JobExpirationCountdownProps {
  expirationDate: string;
  className?: string;
  showIcon?: boolean;
  variant?: 'default' | 'outline' | 'destructive';
  asText?: boolean; // New prop to render as plain text instead of badge
}

export function JobExpirationCountdown({ 
  expirationDate, 
  className = "",
  showIcon = true,
  variant = 'default',
  asText = false
}: JobExpirationCountdownProps) {
  const [timeRemaining, setTimeRemaining] = useState(() => 
    getTimeUntilExpiration(expirationDate)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = getTimeUntilExpiration(expirationDate);
      setTimeRemaining(remaining);
      
      // Clear interval if expired
      if (remaining.isExpired) {
        clearInterval(interval);
      }
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [expirationDate]);

  if (timeRemaining.isExpired) {
    if (asText) {
      return (
        <span className={`${className} text-red-600 animate-pulse flex items-center`}>
          {showIcon && <AlertTriangle className="w-4 h-4 mr-2" />}
          Expired
        </span>
      );
    }
    return (
      <Badge variant="destructive" className={`${className} animate-pulse`}>
        {showIcon && <AlertTriangle className="w-3 h-3 mr-1" />}
        Expired
      </Badge>
    );
  }

  // Determine urgency level
  const totalHours = timeRemaining.hours;
  let urgencyVariant = variant;
  let urgencyClass = '';
  let textUrgencyClass = '';

  if (totalHours <= 24) {
    urgencyVariant = 'destructive';
    urgencyClass = 'animate-pulse';
    textUrgencyClass = 'text-red-600 animate-pulse';
  } else if (totalHours <= 72) {
    urgencyClass = 'text-orange-600 border-orange-300';
    textUrgencyClass = 'text-orange-600';
  }

  const formatTime = () => {
    if (timeRemaining.hours < 1) {
      return `${timeRemaining.minutes}m`;
    } else if (timeRemaining.hours < 24) {
      return `${timeRemaining.hours}h ${timeRemaining.minutes}m`;
    } else {
      const days = Math.floor(timeRemaining.hours / 24);
      const hours = timeRemaining.hours % 24;
      if (days < 7) {
        return `${days}d ${hours}h`;
      } else {
        const weeks = Math.floor(days / 7);
        const remainingDays = days % 7;
        return `${weeks}w ${remainingDays}d`;
      }
    }
  };

  if (asText) {
    return (
      <span className={`${className} ${textUrgencyClass} transition-all duration-300 flex items-center`}>
        {showIcon && <Clock className="w-4 h-4 mr-2" />}
        {formatTime()} left
      </span>
    );
  }

  return (
    <Badge 
      variant={urgencyVariant} 
      className={`${className} ${urgencyClass} transition-all duration-300`}
    >
      {showIcon && <Clock className="w-3 h-3 mr-1" />}
      {formatTime()} left
    </Badge>
  );
}
